import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AdsService } from "../services/ads.service";
import { CreateAdDto, UpdateAdDto, UpdateItemDetailsDto } from "../dto/ads.dto";
import { DEFAULT_LIMIT } from "../../../shared/dto/pagination.dto";

export const useGetAdsHook = (
  event: string,
  page: number,
  limit: number = DEFAULT_LIMIT
) => {
  return useQuery({
    queryKey: ["ads", event, page, limit],
    queryFn: () => {
      return AdsService.getAds(event, page, limit);
    },
    placeholderData: keepPreviousData,
  });
};

export const useGetAdByIdHook = (id: string, event: string) => {
  return useQuery({
    queryKey: ["ads", "detail", id, event],
    queryFn: () => AdsService.getAdById(id, event),
    enabled: !!id && !!event,
  });
};

export const useUpdateItemDetailsHook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      event,
      data,
    }: {
      id: string;
      event: string;
      data: UpdateItemDetailsDto;
    }) => AdsService.updateItemDetails(id, event, data),
    onSuccess: (updated, { id, event }) => {
      queryClient.setQueryData(["ads", "detail", id, event], updated);
      queryClient.invalidateQueries({ queryKey: ["ads", event] });
    },
  });
};

export const useDeleteItemDetailsHook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, event }: { id: string; event: string }) =>
      AdsService.deleteItemDetails(id, event),
    onSuccess: (_, { event }) => {
      queryClient.invalidateQueries({ queryKey: ["ads", event] });
      queryClient.invalidateQueries({ queryKey: ["hosters"] });
    },
  });
};

export const useCreateAdHook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateAdDto) => AdsService.createAd(data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ads"] }),
  });
};

export const useUpdateAdHook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAdDto }) =>
      AdsService.updateAd(id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ads"] }),
  });
};

export const useDeleteAdHook = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => AdsService.deleteAd(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["ads"] }),
  });
};
